import * as React from "react";
import {
  Container,
  Typography,
  Alert,
  TextField,
  LinearProgress,
  Box,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CircleIcon from "@mui/icons-material/Circle";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import AccessContainer from "../common/AccessContainer";

/** Stable toolbar to avoid remounting/focus loss */
const SearchToolbar = React.memo(function SearchToolbar({
  toValue,
  onChangeTo,
  usersValue,
  onChangeUsers,
  unreadOnly,
  onToggleUnread,
  loading,
}) {
  return (
    <div
      style={{
        padding: 16,
        backgroundColor: "#f5f5f5",
        borderBottom: "1px solid #e0e0e0",
        display: "grid",
        gridTemplateColumns: "1fr auto auto",
        gap: 12,
        alignItems: "center",
      }}
    >
      <Typography variant="h6">Notifications</Typography>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={unreadOnly}
              onChange={(e) => onToggleUnread(e.target.checked)}
              color="primary"
            />
          }
          label={unreadOnly ? "Unread" : "All"}
        />
        <TextField
          size="small"
          placeholder="Search To (name or number)…"
          value={toValue}
          onChange={(e) => onChangeTo(e.target.value)}
          inputProps={{ "aria-label": "Search recipient name or number" }}
        />
        <TextField
          size="small"
          placeholder="Search Users (notify emails)…"
          value={usersValue}
          onChange={(e) => onChangeUsers(e.target.value)}
          inputProps={{ "aria-label": "Search notify users" }}
        />
      </Box>

      <div style={{ gridColumn: "1 / -1", height: 4 }}>
        <LinearProgress
          style={{ opacity: loading ? 1 : 0, transition: "opacity .2s" }}
        />
      </div>
    </div>
  );
});

const Notifications = () => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  // Two independent inputs + their debounced values
  const [qToInput, setQToInput] = React.useState("");
  const [qUsersInput, setQUsersInput] = React.useState("");
  const [qTo, setQTo] = React.useState("");
  const [qUsers, setQUsers] = React.useState("");

  // Unread toggle (default on)
  const [unreadOnly, setUnreadOnly] = React.useState(true);

  const [user] = useSessionStorage("user", null);

  // Debounce both inputs
  React.useEffect(() => {
    const t = setTimeout(() => setQTo(qToInput), 300);
    return () => clearTimeout(t);
  }, [qToInput]);

  React.useEffect(() => {
    const t = setTimeout(() => setQUsers(qUsersInput), 300);
    return () => clearTimeout(t);
  }, [qUsersInput]);

  // Pre-populate notifyUsers with session email
  React.useEffect(() => {
    if (user?.userDetails) {
      setQUsersInput(user.userDetails);
      setQUsers(user.userDetails);
    }
  }, [user]);

  // AbortController to cancel stale fetches
  const abortRef = React.useRef(null);

  const fetchConversations = React.useCallback(
    async (userObj, qToVal = "", qUsersVal = "", unread = true) => {
      if (!userObj) return;
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const res = await fetch("/api/MessageHub/GetMessageHubMessages", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sender: userObj.userDetails,
            qTo: qToVal,
            qUsers: qUsersVal,
            unreadOnly: unread,
          }),
          signal: controller.signal,
        });
        if (!res.ok)
          throw new Error(`Failed to fetch messages: ${res.statusText}`);
        const data = await res.json();
        setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Failed to fetch conversations:", err);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    []
  );

  // Fetch when user / queries / unread toggle change
  React.useEffect(() => {
    if (user) fetchConversations(user, qTo, qUsers, unreadOnly);
  }, [user, qTo, qUsers, unreadOnly, fetchConversations]);

  const renderMessagePreview = (replies) => {
    if (!replies?.length) return "No messages yet";
    const lastMessage = replies[replies.length - 1]?.message || "";
    const words = String(lastMessage).split(" ");
    return words.length > 10
      ? `${words.slice(0, 10).join(" ")}...`
      : lastMessage;
  };

  const renderDate = (ts) =>
    ts ? new Date(ts * 1000).toLocaleString() : "N/A";

  const renderRecipient = (name, number) =>
    name || (number ? `+${number}` : "—");

  const columns = React.useMemo(
    () => [
      {
        field: "recipientName",
        headerName: "To",
        width: 220,
        renderCell: (p) => renderRecipient(p.row.recipientName, p.row.to),
      },
      {
        field: "from",
        headerName: "From",
        width: 160,
        renderCell: (p) => `${p.row.from}`,
      },
      {
        field: "messagePreview",
        headerName: "Message",
        width: 200,
        renderCell: (p) => renderMessagePreview(p.row.replies),
      },
      {
        field: "notifyUsers",
        headerName: "Users",
        width: 260,
        renderCell: (p) => {
          const text = (p.row.notifyUsers || []).join(", ");
          return (
            <Typography variant="caption" noWrap title={text}>
              {text || "—"}
            </Typography>
          );
        },
      },
      {
        field: "lastMessageAt",
        headerName: "Last Message",
        width: 200,
        renderCell: (p) => renderDate(p.row.lastMessageAt),
      },
      {
        field: "isSeen",
        headerName: "Read",
        width: 90,
        align: "center",
        headerAlign: "center",
        renderCell: (p) =>
          !p.row.isSeen && (
            <CircleIcon sx={{ color: "#3374E2", fontSize: 15 }} />
          ),
      },
    ],
    []
  );

  return (
    <AccessContainer
      customStyle={{ maxWidth: "100%", width: "100%" }}
      allowedRoles={["NexusAdmin", "MessageHub"]}
    >
      <Container>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontSize: 14 }}>
            Filter by recipient (name/number), notify users (emails), and toggle
            between unread-only vs all.
          </Typography>
        </Alert>

        <div style={{ height: 600, width: "100%", marginTop: 16 }}>
          <DataGrid
            slots={{ toolbar: SearchToolbar }}
            slotProps={{
              toolbar: {
                toValue: qToInput,
                onChangeTo: setQToInput,
                usersValue: qUsersInput,
                onChangeUsers: setQUsersInput,
                unreadOnly,
                onToggleUnread: setUnreadOnly,
                loading,
              },
            }}
            getRowId={(row) => row.conversationId}
            rows={rows}
            columns={columns}
            pageSizeOptions={[5]}
            hideFooterPagination
            loading={loading}
            disableRowSelectionOnClick
            onRowClick={(params) =>
              (window.location.href = `/MessageHub/Conversation/${params.row.conversationId}`)
            }
          />
        </div>
      </Container>
    </AccessContainer>
  );
};

export default Notifications;
